'use client';
import { CheckCircle, Users, DollarSign, Zap, PiggyBank } from 'lucide-react';

import Download from '@/components/common/download';
import ContactUsCTA from '@/components/common/ContactCTA';
import { Feature } from '@/components/common/feature-variants';

export default function BecomeAgentPage() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-bold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary">
              Become a <span className="text-primary">PayStop Agent</span>
            </h1>

            <p className="max-w-3xl mx-auto text-foreground/70">
              Join the PayStop network and start earning by providing financial services to your
              community. Whether you’re an individual entrepreneur, a local business owner, or
              looking to expand your financial service offerings, becoming an agent with PayStop
              offers you a rewarding opportunity to grow your business while helping others access
              essential financial services.
            </p>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container container-md">
          {/* Why Become an Agent */}
          <div className="space-y-12">
            <div className="space-y-8">
              <div className="flex items-center justify-center space-x-4">
                <h2 className="font-semibold title">Why Become a PayStop Agent?</h2>
              </div>
              <p className="max-w-3xl mx-auto text-center text-foreground/70">
                At PayStop, we empower you to offer a wide range of financial services to your
                customers, from deposits and withdrawals to transfers between digital banking
                accounts, mobile wallets, and traditional bank accounts. By joining our agent
                network, you’re providing a much-needed service to your community while earning
                competitive rewards.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-20 md:grid-cols-2 ">
              {[
                {
                  title: 'Earn Rewarding Commissions',
                  icon: DollarSign,
                  description:
                    'You’ll earn commission on every transaction processed through your PayStop account. This includes deposits, withdrawals, and money transfers. Plus, if you invite others to join the network, you can earn even more.',
                },
                {
                  title: 'Expand Your Business',
                  icon: Zap,
                  description:
                    'Tap into a new revenue stream by becoming part of the digital financial ecosystem. With PayStop, you can attract new customers and provide additional services, helping your business grow.',
                },
                {
                  title: 'Local Impact',
                  icon: Users,
                  description:
                    'You’ll be providing an essential service to your community, enabling people to access their digital bank accounts, mobile wallets, and more—without needing to go to a traditional bank.',
                },
                {
                  title: 'Easy Setup',
                  icon: CheckCircle,
                  description:
                    'Becoming an agent is simple. With our user-friendly app, you can manage your operations from anywhere and start offering services right away.',
                },
              ].map((item, index) => (
                <Feature
                  key={index}
                  description={item.description}
                  icon={<item.icon className="text-primary" />}
                  title={item.title}
                  variant="horizontal"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-md bg-slate-50 dark:bg-background">
        <div className="container container-md">
          {/* How It Works */}
          <div className="space-y-12">
            <div className="flex flex-col items-center justify-center space-x-4 space-y-5">
              <h2 className="font-semibold title">How It Works</h2>
              <p className="max-w-3xl mx-auto text-center text-foreground/70">
                Getting started as an agent is easy. Here's how it works:
              </p>
            </div>
            <div className="grid grid-cols-1 gap-20 md:grid-cols-2 ">
              {[
                {
                  title: 'Sign Up as a Super Agent',
                  icon: CheckCircle,
                  description:
                    'To get started, download the PayStop app, complete the registration process, and fund your Super Agent account through a commercial bank. After registering, you’ll become the primary agent in your area, capable of managing transactions for your customers.',
                },
                {
                  title: 'Invite Sub-Agents and Agent Admins',
                  icon: Users,
                  description:
                    'Once you’ve signed up as a Super Agent, you can invite Sub-Agents to assist with customer transactions. You can also appoint Agent Admins to help manage the day-to-day operations of your network.',
                },
                {
                  title: 'Provide Services',
                  icon: DollarSign,
                  description:
                    'As an agent, you will help your customers with various services, including deposits, withdrawals, and transfers between accounts (digital and traditional).',
                },
                {
                  title: 'Earn Rewards',
                  icon: PiggyBank,
                  description:
                    'Each time a customer makes a transaction, you’ll earn a commission based on the transaction value. The more transactions you facilitate, the more you earn.',
                },
              ].map((item, index) => (
                <Feature
                  key={index}
                  description={item.description}
                  icon={<item.icon className="text-primary" />}
                  title={item.title}
                  variant="horizontal"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-md bg-primary-50 dark:bg-background">
        <div className="container container-md">
          {/* How It Works */}
          <div className="space-y-12">
            <div className="flex flex-col items-center justify-center space-x-4 space-y-5">
              <h2 className="font-semibold title">Requirements to Become an Agent </h2>
              <p className="max-w-3xl mx-auto text-center text-foreground/70">
                Becoming a PayStop agent is open to individuals and businesses who meet the
                following requirements:
              </p>
            </div>
            <div className="grid grid-cols-1 gap-20 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: 'Start-Up Capital',
                  icon: CheckCircle,
                  description:
                    'To join as a Super Agent, you must fund your account through a commercial bank with an initial deposit to start providing services.',
                },
                {
                  title: 'Smartphone',
                  icon: Users,
                  description:
                    ' You’ll need a smartphone (Android or iOS) to download and use the PayStop app, which allows you to manage transactions, track earnings, and monitor your agent network.',
                },
                {
                  title: 'Location',
                  icon: DollarSign,
                  description:
                    'Ideally, your business should be in a location that allows you to serve your community effectively, especially in areas with high foot traffic where people can easily access your services.',
                },
                {
                  title: 'Valid Phone Number',
                  icon: PiggyBank,
                  description:
                    'Your phone number will be used to link your PayStop account. It must be valid and operational for communication purposes.',
                },
                {
                  title: 'Business Registration (if applicable)',
                  icon: PiggyBank,
                  description:
                    "If you're a business, ensure you have the necessary business registration and legal requirements in place. You’ll be able to offer services as a recognised business agent.",
                },
              ].map((item, index) => (
                <Feature
                  key={index}
                  description={item.description}
                  icon={<item.icon className="text-primary" />}
                  title={item.title}
                  variant="horizontal"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-md">
        <div className="container space-y-12 container-max">
          <h2 className="mb-6 font-semibold title">How to Get Started</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: 'Sign Up',
                description:
                  'Download the PayStop app from the Google Play Store or App Store and begin your registration. Fill out the necessary details to create your account and fund your account through a commercial bank.',
              },
              {
                title: 'Invite Sub-Agents and Admins',
                description:
                  "Once you've registered as a Super Agent, invite Sub-Agents and appoint Agent Admins to assist with managing your customer transactions. Set up your network and start facilitating financial services.",
              },
              {
                title: 'Start Serving Customers',
                description:
                  "With your account funded, you can begin accepting deposits, withdrawals, and transfers from customers. You'll facilitate transactions between digital bank accounts, mobile wallets, and traditional bank accounts.",
              },
              {
                title: 'Earn Commissions',
                description:
                  "You'll earn commissions for every transaction you facilitate. Whether a customer is making a deposit, withdrawal, or transfer, you'll earn a percentage of the transaction fee.",
              },
            ].map((step, index) => (
              <Feature
                key={index}
                description={step.description}
                title={step.title}
                showIcon={false}
                variant="numbered"
                icon={''}
                index={index}
                className="shadow-md"
              />
            ))}
          </div>
        </div>
      </section>
      <section className="section-sm">
        <div className="container space-y-12 container-min">
          <div className="text-center">
            <h2 className="font-bold title">Support and Training</h2>
            <p className="mt-2 text-lg text-foreground/70 ">
              We believe in setting you up for success. That’s why we offer:
            </p>
          </div>

          <div className="grid justify-center gap-10 md:grid-cols-2">
            {[
              {
                title: 'Training',
                icon: PiggyBank,
                description:
                  'Once you become an agent, we provide training on how to use the PayStop app, manage transactions, and monitor your earnings. We also ensure you understand how to safely facilitate transactions and provide excellent customer service.',
              },
              {
                title: ' Customer Support',
                icon: PiggyBank,
                description:
                  'We offer  24/7 support  to help you with any questions or issues you may face. Our team is ready to assist you with app usage, transaction queries, or anything else you may need.',
              },
            ].map((item, index) => (
              // <div key={index} className="flex items-start space-x-4 group">
              //   <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 transition-colors duration-300 rounded-full bg-primary/10 group-hover:bg-primary/20">
              //     <item.icon className="w-6 h-6 text-primary" />
              //   </div>
              //   <div>
              //     <h3 className="mb-2 text-lg font-semibold">{item.title}</h3>
              //     <p className="max-w-2xl text-sm text-foreground/70">{item.description}</p>
              //   </div>
              // </div>
              <Feature
                key={index}
                description={item.description}
                icon={<item.icon className="text-primary" />}
                title={item.title}
                variant="bordered"
              />
            ))}
          </div>
        </div>
      </section>
      <section className="">
        <div className="container space-y-24 container-max">
          {/* Call to Action */}
          <div className="relative px-8 py-16 overflow-hidden bg-gradient-to-b from-primary/20 to-background/5 rounded-3xl">
            <div className="space-y-12">
              <div className="relative z-10 space-y-4 text-center">
                <h2 className="font-bold text-primary title">Start Earning Today!</h2>
                <p className="max-w-3xl mx-auto text-foreground/70">
                  Becoming a PayStop agent is your gateway to offering essential financial services
                  while earning attractive rewards. Whether you're a small business or a growing
                  entrepreneur, PayStop provides the tools you need to succeed. Join us and start
                  making an impact in your community today!
                </p>
              </div>

              {/* Contact Us */}
              <ContactUsCTA
                className="bg-background border-primary-100"
                description={'Have questions or need assistance? We’re here to help!'}
                email={'  support@paystop.io'}
                office={'23 Business Avenue, City, Country'}
                phone={'+237 676962723'}
                showOffice={false}
                title={'Get in Touch'}
              />
            </div>
          </div>
        </div>
      </section>

      <Download />
    </>
  );
}
